import { EmptyState } from '@atlas/ui-components'
import { Skeleton } from '@atlas/ui-primitives'
import { BrandResultCard } from './BrandResultCard'
import type { BrandResult } from './brand-catalog'

const GRID = 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'

export interface BrandResultsProps {
	brands: BrandResult[]
	/** How many brands were in geographic scope before the search narrowed them. */
	totalInScope: number
	/** The active scope in words, e.g. `US` or `NORAM` - `null` when unfiltered. */
	scopeLabel: string | null
	query: string
	/** Geography code carried into each brand page — a market, or the region when only one is chosen. */
	market: string | null
	isLoading: boolean
	onClearSearch: () => void
}

interface ResultCountProps {
	shown: number
	totalInScope: number
	scopeLabel: string | null
	query: string
}

/** Count line above the grid: reports the search narrowing separately from the scope. */
function ResultCount({ shown, totalInScope, scopeLabel, query }: ResultCountProps) {
	const noun = totalInScope === 1 ? 'brand' : 'brands'
	const where = scopeLabel ? ` in ${scopeLabel}` : ''
	const needle = query.trim()
	return (
		<p className="mb-4 text-sm text-ds-text-muted">
			{needle === ''
				? `${totalInScope} ${noun}${where}`
				: `${shown} of ${totalInScope} ${noun}${where} matching “${needle}”`}
		</p>
	)
}

/**
 * The Explore results grid. Distinguishes the two empty outcomes that mean different
 * things: no brands classified in this market yet (a coverage gap) versus none matching
 * the search (a query to relax).
 *
 * The reset action is worded by outcome ("Show all brands"), not by mechanism, so it does
 * not collide with the search box's own clear button for anyone navigating by label.
 */
export function BrandResults({
	brands,
	totalInScope,
	scopeLabel,
	query,
	market,
	isLoading,
	onClearSearch,
}: BrandResultsProps) {
	if (isLoading) {
		return (
			<div className={GRID} aria-busy="true">
				{Array.from({ length: 8 }, (_, i) => (
					<Skeleton key={i} className="h-22 rounded-ds-card" />
				))}
			</div>
		)
	}

	if (totalInScope === 0) {
		return (
			<EmptyState
				className="bg-transparent p-8"
				title={`No brands are classified ${scopeLabel ? `in ${scopeLabel}` : 'yet'}, so there is nothing to browse here.`}
			/>
		)
	}

	if (brands.length === 0) {
		return (
			<EmptyState
				className="bg-transparent p-8"
				variant="filtered"
				title={`No brands match “${query.trim()}”.`}
				action={
					<button
						type="button"
						onClick={onClearSearch}
						className="text-ds-sm text-ds-primary underline-offset-2 hover:underline"
					>
						Show all brands
					</button>
				}
			/>
		)
	}

	return (
		<>
			<ResultCount shown={brands.length} totalInScope={totalInScope} scopeLabel={scopeLabel} query={query} />
			<div className={GRID}>
				{brands.map((b) => (
					<BrandResultCard key={b.brandId} brand={b} market={market} />
				))}
			</div>
		</>
	)
}
